'use client';

import { useMemo } from 'react';

import { type VariantProps } from 'tailwind-variants';

import { type ComponentProps, type Component } from '../../types/component';
import { tv } from '../../utils/tv';
import {
  ListContext,
  type ListContextValue,
  useListContext,
} from './ListContext';

const listItemClasses = {
  root: 'zds-list-item-root',
  content: 'zds-list-item-content',
} as const;

const listItemStyles = tv({
  slots: {
    root: [listItemClasses.root],
    content: [listItemClasses.content, 'box-border flex w-full gap-4 px-4'],
  },
  variants: {
    density: {
      compact: { content: 'py-1' },
      default: { content: 'py-2' },
      spacious: { content: 'py-3' },
    },
    densityXs: {
      compact: { content: 'xs:py-1' },
      default: { content: 'xs:py-2' },
      spacious: { content: 'xs:py-3' },
    },
    densitySm: {
      compact: { content: 'sm:py-1' },
      default: { content: 'sm:py-2' },
      spacious: { content: 'sm:py-3' },
    },
    densityMd: {
      compact: { content: 'md:py-1' },
      default: { content: 'md:py-2' },
      spacious: { content: 'md:py-3' },
    },
    densityLg: {
      compact: { content: 'lg:py-1' },
      default: { content: 'lg:py-2' },
      spacious: { content: 'lg:py-3' },
    },
    densityXl: {
      compact: { content: 'xl:py-1' },
      default: { content: 'xl:py-2' },
      spacious: { content: 'xl:py-3' },
    },
    alignItems: {
      top: { content: 'items-start' },
      center: { content: 'items-center' },
    },
    color: {
      neutral: {},
      'static-white': { root: 'text-static-white-main' },
      'accent-primary': {},
    },
    shape: {
      square: {},
      rounded: {
        root: 'z--list-list-item-rounded-border-radius',
        content: 'z--list-list-item-rounded-border-radius',
      },
    },
    disablePadding: {
      true: { content: 'p-0' },
    },
  },
});

type Variant = VariantProps<typeof listItemStyles>;

interface ListItemBaseProps {
  /**
   * Remove default padding. Use when `List.ItemButton` is a child so the
   * button owns the full pressable area.
   * @default false
   */
  disablePadding?: boolean;
  /** Vertical alignment of children along the row. Inherits from `List` context, falls back to `'top'`. */
  alignItems?: 'top' | 'center';
  /** Override colour from `List` context for this item and its descendants. */
  color?: Variant['color'];
  /** Corner shape. Inherits from `List` context, falls back to `'square'`. */
  shape?: Variant['shape'];
}

type ListItemProps = ComponentProps<ListItemBaseProps, 'li'>;

type ListItemComponent = Component<ListItemBaseProps, 'li'>;

const ListItem: ListItemComponent = (props) => {
  const {
    children,
    disablePadding = false,
    alignItems: alignItemsProp,
    color: colorProp,
    shape: shapeProp,
    className,
    ref,
    ...rest
  } = props;

  const context = useListContext();
  const density = context?.density ?? 'default';
  const responsiveDensity = context?.responsiveDensity;
  const alignItems = alignItemsProp ?? context?.alignItems ?? 'center';
  const color = colorProp ?? context?.color ?? 'neutral';
  const shape = shapeProp ?? context?.shape ?? 'square';

  const itemContext = useMemo<ListContextValue>(
    () => ({
      density,
      responsiveDensity,
      color,
      alignItems,
      shape,
    }),
    [density, responsiveDensity, color, alignItems, shape]
  );

  const styles = listItemStyles({
    density: disablePadding ? undefined : density,
    densityXs: disablePadding ? undefined : responsiveDensity?.xs,
    densitySm: disablePadding ? undefined : responsiveDensity?.sm,
    densityMd: disablePadding ? undefined : responsiveDensity?.md,
    densityLg: disablePadding ? undefined : responsiveDensity?.lg,
    densityXl: disablePadding ? undefined : responsiveDensity?.xl,
    alignItems,
    color,
    shape,
    disablePadding,
  });

  return (
    <ListContext.Provider value={itemContext}>
      <li {...rest} ref={ref} className={styles.root({ className })}>
        <div className={styles.content()}>{children}</div>
      </li>
    </ListContext.Provider>
  );
};

ListItem.displayName = 'List.Item';

export {
  ListItem,
  type ListItemProps,
  type ListItemBaseProps,
  listItemClasses,
};
